import {zodResolver} from "@hookform/resolvers/zod";
import {CalendarIcon, Loader2, XIcon} from "lucide-react";
import {useEffect, useMemo, useRef, useState} from "react";
import {type UseFormReturn, useForm} from "react-hook-form";

import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar";
import {DatePickerWithRange} from "@/components/ui/date-range-picker";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {Switch} from "@/components/ui/switch";
import {Textarea} from "@/components/ui/textarea";
import {moment, resolveTimezone} from "@/src/modules/core/lib";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useStoredCompany} from "@/src/modules/company/store";
import {useStoredMeProperties} from "@/src/modules/core/store/me";
import type {VehicleSafelistingsAccountability} from "@/src/modules/vehicles/types";
import {HouseholdCombobox} from "@/src/modules/households/components/combobox";
import {VehiclesCombobox} from "@/src/modules/vehicles/components/vehicles-combobox";
import type {Vehicle} from "@/src/modules/vehicles/types";
import {HouseholdAmenityRestrictionBanner} from "@/src/modules/households/components/household-amenity-restriction-banner";
import {isAmenityKeyRestricted} from "@/src/modules/households/lib/household-amenity-restriction";
import type {AmenityRestrictionCheckSource} from "@/src/modules/households/lib/household-amenity-restriction";
import {FieldHelpLabel} from "@/src/modules/help/components/field-help";
import {HelpTarget} from "@/src/modules/help/components/help-target";

import {useStoredUserProperty} from "../../core/store/user-property";
import {usePropertyTypes} from "@/src/modules/properties/hooks/use-property-types";
import {Household} from "../../households/types";
import {buildDaysFromRange} from "../lib/build-days-from-range";
import {computeLimitBanner} from "../lib/compute-limit-banner";
import {
  computeSafelistingCoveragePreview,
  getSafelistingCoverageFormCopy,
} from "../lib/compute-safelisting-coverage-preview";
import {getSafelistingFormDefaultValues} from "../lib/get-safelisting-form-default-values";
import {SAFELISTING_FORM_HELP_KEYS} from "../lib/safelisting-help";
import {
  getSafelistingRangeHoursFromProperty,
  mergePropertyRulesFromMe,
} from "../lib/get-safelisting-range-hours-from-property";
import {CITATION_FULL_DAY_RANGE} from "@/src/modules/properties/lib/citation-range-hours";
import {
  getSafelistingEditDateLocks,
  isLockedCoveredCalendarDate,
  isLockedExcludedDate,
  isSafelistingCalendarDateSelectable,
  rangePreservesLockedCoverage,
  validateSafelistingEditDateLocks,
  validateSafelistingNoPastCoverage,
} from "../lib/safelisting-edit-date-locks";
import {
  detectCoverageAnchorMismatch,
  getCoverageAnchorQuickPickLabels,
  getSafelistingQuickPickAnchors,
  singleAnchorDatesRange,
} from "../lib/coverage-anchor-hints";
import {formatUsPhone, normalizeUsPhoneDigits} from "@/src/modules/properties/lib/us-phone";

import {safelistingFormSchema} from "../schemas";
import type {DatesRange, Safelisting, SafelistingFormValues} from "../types";
import {SafelistingCoveragePreviewPanel} from "./safelisting-coverage-preview";
import {SafelistingCoverageRulesCallout} from "./safelisting-coverage-rules-callout";
import {SafelistingCoverageAnchorQuickPicks} from "./safelisting-coverage-anchor-quick-picks";
import {SafelistingOverrideHelpCard, SAFELISTING_OVERRIDE_FIELD_HINT_CLASS, SAFELISTING_OVERRIDE_SWITCH_LABEL_CLASS} from "./safelisting-override-help-card";
import {SAFELISTING_OVERRIDE_FIELD_HINTS} from "../lib/safelisting-override-help";
import {SafelistingLimitBanner} from "./safelisting-limit-banner";

const SHEET_FORM_X = "px-4 @min-[36rem]:px-6";
const SHEET_STICKY_FOOTER_X = "-mx-4 px-4 @min-[36rem]:-mx-6 @min-[36rem]:px-6";
/** Tighter rhythm within the main permit flow (coverage → permit → recipient → notes). */
const SHEET_PERMIT_FLOW_GAP = "flex flex-col gap-4";
/** Split recipient fields at wide sheet panel width — container query, not viewport. */
const RECIPIENT_FIELDS_ROW = "grid grid-cols-1 gap-4 @min-[36rem]:grid-cols-2";

type CoverageAnchorSelectionSource = "default" | "quick_pick" | "calendar";

type SafelistingFormProps = {
  safelisting?: Safelisting | null;
  /** Full accountability payload from GET `/api/v1/accountability`; null when embed is unavailable. */
  accountability: VehicleSafelistingsAccountability | null;
  onSubmit: (payload: SafelistingFormValues) => void | Promise<void>;
  initialHousehold?: Pick<Household, "house_hold_id" | "name"> | null;
  lockHouseholdSelection?: boolean;
  /** Full household row for read-only combobox label (e.g. resident's assigned_to). */
  lockedHouseholdDisplay?: Household | null;
  onHouseholdChange?: (householdId: number | null) => void;
  /** Resident create (no vehicle context): show vehicle picker instead of household. */
  contextVehicle?: Vehicle | null;
  onContextVehicleChange?: (vehicle: Vehicle | null) => void;
  /** Residents only — officers always pick household; vehicle comes from detail/dispatch context. */
  hideHouseholdField?: boolean;
  /** Called with the form instance when ready (e.g. for vehicle action sheet ref). */
  onInit?: (form: UseFormReturn<SafelistingFormValues>) => void;
};

function toYyyyMmDd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function isDateInRange(date: Date, from: Date, to: Date): boolean {
  const d = toYyyyMmDd(date);
  const f = toYyyyMmDd(from);
  const t = toYyyyMmDd(to);
  return d >= f && d <= t;
}

export function SafelistingForm({
  safelisting,
  accountability,
  onSubmit,
  initialHousehold,
  lockHouseholdSelection = false,
  lockedHouseholdDisplay,
  onHouseholdChange,
  contextVehicle = null,
  onContextVehicleChange,
  hideHouseholdField = false,
  onInit,
}: SafelistingFormProps) {
  const me = useStoredMe();
  const showStaffOverrideFields = !me?.isResident;
  const showStaffNoteFields = !me?.isResident;
  const userProperty = useStoredUserProperty()!;
  const meProperties = useStoredMeProperties();
  const company = useStoredCompany();
  const {propertyTypes, isPending: propertyTypesPending} = usePropertyTypes();
  const propertyForRules = useMemo(
    () => mergePropertyRulesFromMe(userProperty, meProperties) ?? userProperty,
    [userProperty, meProperties],
  );
  const propertyTimeZone = resolveTimezone(company?.timezone);
  const [excludePopoverOpen, setExcludePopoverOpen] = useState(false);
  const [coverageAnchorSelectionSource, setCoverageAnchorSelectionSource] =
    useState<CoverageAnchorSelectionSource>("default");
  const didApplyLikelyAnchorDefault = useRef(false);
  const remaining = accountability?.remaining ?? null;

  const defaultValues = useMemo(() => {
    const values = getSafelistingFormDefaultValues(safelisting);
    if (!safelisting && initialHousehold) {
      values.household = initialHousehold;
    }
    return values;
  }, [safelisting, initialHousehold]);

  const form = useForm<SafelistingFormValues>({
    resolver: zodResolver(safelistingFormSchema),
    defaultValues,
  });
  const {
    isSubmitting,
    dirtyFields: _dirtyFields,
  } = form.formState;

  const datesRange = form.watch("dates_range");
  const excludedDates = form.watch("excluded_dates");
  const overrideLimit = form.watch("override_limit");
  const selectedHousehold = form.watch("household");
  const effectiveHousehold =
    selectedHousehold ??
    (lockHouseholdSelection ? lockedHouseholdDisplay ?? initialHousehold : null);
  const selectedHouseholdId = effectiveHousehold?.house_hold_id ?? null;
  const hasSelectedHousehold = selectedHouseholdId != null;
  const hasContextVehicle = contextVehicle != null;
  const canShowPermitFlow = hideHouseholdField ? hasContextVehicle : hasSelectedHousehold;
  const from = datesRange?.from;
  const to = datesRange?.to;
  const rangeComplete = Boolean(from && to);
  const rangeDayCount =
    rangeComplete && from && to
      ? Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1
      : 0;
  const coveredDaysInRange = Math.max(0, rangeDayCount - excludedDates.length);
  const canAddExclude = rangeComplete && coveredDaysInRange > 1;
  let netNewPermitDays = rangeDayCount - excludedDates.length;
  if (safelisting) {
    netNewPermitDays -= safelisting.days.length;
  }
  const permitDays = Math.max(0, netNewPermitDays);
  const isExeeding = remaining != null && remaining < permitDays;
  const limitBannerDescriptor = useMemo(
    () => computeLimitBanner(accountability, permitDays),
    [accountability, permitDays],
  );
  const expandOverrideRecipe =
    showStaffOverrideFields &&
    (limitBannerDescriptor?.variant === "warning" ||
      limitBannerDescriptor?.variant === "destructive");
  const isSubmitDisabled =
    (isExeeding && (showStaffOverrideFields ? !overrideLimit : true)) ||
    (!safelisting &&
      isAmenityKeyRestricted(
        effectiveHousehold as AmenityRestrictionCheckSource | null,
        "safelisting",
      ));

  const rangeHours = useMemo(() => {
    const resolved = getSafelistingRangeHoursFromProperty(propertyForRules, propertyTypes);
    if (resolved) return resolved;
    if (propertyTypesPending) return null;
    return {...CITATION_FULL_DAY_RANGE};
  }, [propertyForRules, propertyTypes, propertyTypesPending]);
  const coverageFormCopy = useMemo(() => {
    if (!rangeHours) return null;
    return getSafelistingCoverageFormCopy(rangeHours);
  }, [rangeHours]);
  const coverageDays = useMemo(() => {
    if (!from || !to) return [];
    return buildDaysFromRange({
      dates_range: {from, to},
      excluded_dates: excludedDates,
    });
  }, [from, to, excludedDates]);
  const coveragePreview = useMemo(() => {
    if (!rangeHours) return null;
    return computeSafelistingCoveragePreview(rangeHours, coverageDays, propertyTimeZone);
  }, [rangeHours, coverageDays, propertyTimeZone]);
  const editDateLocks = useMemo(
    () => getSafelistingEditDateLocks(safelisting, rangeHours, propertyTimeZone),
    [safelisting, rangeHours, propertyTimeZone],
  );
  const hasLockedPastCoverage = (editDateLocks?.lockedCoveredDates.length ?? 0) > 0;
  const todayAnchorInPropertyTz = moment.tz(propertyTimeZone).format("YYYY-MM-DD");
  const todayCoverageWindowEnded =
    rangeHours != null &&
    !isSafelistingCalendarDateSelectable(
      moment.tz(todayAnchorInPropertyTz, "YYYY-MM-DD", propertyTimeZone).toDate(),
      rangeHours,
      propertyTimeZone,
    );
  const quickPickAnchors = useMemo(() => {
    if (!rangeHours) return null;
    return getSafelistingQuickPickAnchors(rangeHours, propertyTimeZone);
  }, [rangeHours, propertyTimeZone]);
  const anchorQuickPickLabels = useMemo(() => {
    if (!rangeHours) return null;
    return getCoverageAnchorQuickPickLabels(rangeHours, propertyTimeZone);
  }, [rangeHours, propertyTimeZone]);
  const selectedFromAnchor = from ? toYyyyMmDd(from) : null;
  const coverageAnchorMismatch = useMemo(() => {
    if (!rangeHours || safelisting) return null;
    return detectCoverageAnchorMismatch({from, to}, rangeHours, propertyTimeZone);
  }, [from, to, rangeHours, propertyTimeZone, safelisting]);

  const handleExcludeDateSelect = (date: Date | undefined) => {
    if (!date) return;
    const str = toYyyyMmDd(date);
    if (isLockedCoveredCalendarDate(date, editDateLocks)) {
      setExcludePopoverOpen(false);
      return;
    }
    const current = form.getValues("excluded_dates");
    if (current.includes(str)) {
      setExcludePopoverOpen(false);
      return;
    }
    const nextExcluded = [...current, str];
    if (
      !rangePreservesLockedCoverage(
        form.getValues("dates_range"),
        nextExcluded,
        editDateLocks?.lockedCoveredDates ?? [],
      )
    ) {
      form.setError("dates_range", {
        message: "Coverage that has already started cannot be removed from this permit.",
      });
      setExcludePopoverOpen(false);
      return;
    }
    form.clearErrors("dates_range");
    form.setValue("excluded_dates", nextExcluded, {shouldDirty: true});
    setExcludePopoverOpen(false);
  };

  const removeExcludedDate = (dateStr: string) => {
    if (isLockedExcludedDate(dateStr, editDateLocks)) return;
    const nextExcluded = form.getValues("excluded_dates").filter((d) => d !== dateStr);
    if (
      !rangePreservesLockedCoverage(
        form.getValues("dates_range"),
        nextExcluded,
        editDateLocks?.lockedCoveredDates ?? [],
      )
    ) {
      form.setError("dates_range", {
        message: "Coverage that has already started cannot be removed from this permit.",
      });
      return;
    }
    form.clearErrors("dates_range");
    form.setValue("excluded_dates", nextExcluded, {shouldDirty: true});
  };

  useEffect(() => {
    onInit?.(form);
  }, [form, onInit]);

  useEffect(() => {
    if (!initialHousehold) return;
    const current = form.getValues("household");
    if (current?.house_hold_id === initialHousehold.house_hold_id) return;
    form.setValue("household", initialHousehold, {shouldDirty: false, shouldValidate: true});
  }, [form, initialHousehold, lockHouseholdSelection]);

  useEffect(() => {
    onHouseholdChange?.(selectedHouseholdId);
  }, [onHouseholdChange, selectedHouseholdId]);

  useEffect(() => {
    setCoverageAnchorSelectionSource("default");
  }, [selectedHouseholdId]);

  useEffect(() => {
    if (!isExeeding && safelisting == null) {
      form.setValue("override_limit", false);
      form.setValue("override_reason", "");
      form.clearErrors("override_reason");
    }
  }, [isExeeding, safelisting, form]);

  useEffect(() => {
    if (safelisting || didApplyLikelyAnchorDefault.current || propertyTypesPending || !rangeHours) {
      return;
    }
    const defaultAnchor = quickPickAnchors?.primary;
    if (!defaultAnchor) return;
    const current = form.getValues("dates_range");
    if (current?.from || current?.to) return;
    didApplyLikelyAnchorDefault.current = true;
    form.setValue("dates_range", singleAnchorDatesRange(defaultAnchor), {shouldDirty: false});
  }, [safelisting, propertyTypesPending, rangeHours, quickPickAnchors, form]);

  const applyDatesRangeChange = (
    range: DatesRange,
    onDatesRangeChange: (value: DatesRange) => void,
  ) => {
    const excluded = form.getValues("excluded_dates");
    if (
      !rangePreservesLockedCoverage(range, excluded, editDateLocks?.lockedCoveredDates ?? [])
    ) {
      form.setError("dates_range", {
        message: "Coverage that has already started cannot be removed from this permit.",
      });
      return;
    }
    form.clearErrors("dates_range");
    onDatesRangeChange(range);
    if (!range.from && !range.to) {
      form.setValue("excluded_dates", [], {shouldDirty: true});
    } else if (range.from && range.to) {
      const filteredExcluded = excluded.filter((dateStr) => {
        const d = moment(dateStr, "YYYY-MM-DD").toDate();
        return isDateInRange(d, range.from!, range.to!);
      });
      if (filteredExcluded.length !== excluded.length) {
        form.setValue("excluded_dates", filteredExcluded, {shouldDirty: true});
      }
    }
    form.setValue("override_limit", false);
    form.setValue("override_reason", "");
    form.clearErrors("override_reason");
  };

  const applyCoverageAnchor = (
    anchor: string,
    onDatesRangeChange: (value: DatesRange) => void,
    selectionSource: CoverageAnchorSelectionSource = "quick_pick",
  ) => {
    setCoverageAnchorSelectionSource(selectionSource);
    applyDatesRangeChange(singleAnchorDatesRange(anchor), onDatesRangeChange);
  };

  const inlineCoverageAnchorMismatch =
    coverageAnchorSelectionSource !== "quick_pick" ? coverageAnchorMismatch : null;
  const excludeDateWord = coverageFormCopy?.dateWord === "night" ? "night" : "date";
  const excludeDateWordPlural = excludeDateWord === "night" ? "nights" : "dates";

  const handleFormSubmit = async (values: SafelistingFormValues) => {
    const lockError = validateSafelistingEditDateLocks(values, editDateLocks);
    if (lockError) {
      form.setError("dates_range", {message: lockError});
      return;
    }
    const pastCoverageError = validateSafelistingNoPastCoverage(
      values,
      rangeHours,
      propertyTimeZone,
      editDateLocks,
    );
    if (pastCoverageError) {
      form.setError("dates_range", {message: pastCoverageError});
      return;
    }
    await onSubmit(values);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className={`flex flex-col gap-6 pb-4 pt-4 ${SHEET_FORM_X}`}
      >
        {hideHouseholdField ? (
          <FormItem>
            <FormLabel required>Vehicle</FormLabel>
            <FormControl>
              <VehiclesCombobox
                value={contextVehicle}
                onChange={onContextVehicleChange ?? (() => {})}
                houseHoldId={null}
                contextPropertyId={userProperty.id}
                showResidencyBadge
                disabled={onContextVehicleChange == null}
              />
            </FormControl>
          </FormItem>
        ) : (
          <FormField
            control={form.control}
            name="household"
            render={({field}) => {
              const household = field.value as Household | null;

              return (
                <FormItem>
                  <FieldHelpLabel helpKey={SAFELISTING_FORM_HELP_KEYS.household} showIcon={false}>
                    <FormLabel required>Household</FormLabel>
                  </FieldHelpLabel>
                  <FormControl>
                    {lockHouseholdSelection ? (
                      <HouseholdCombobox
                        filter={{context_property_id: userProperty?.id}}
                        value={
                          household ??
                          lockedHouseholdDisplay ??
                          (initialHousehold as Household | null)
                        }
                        onChange={() => {}}
                        disabled
                      />
                    ) : (
                      <HouseholdCombobox
                        filter={{context_property_id: userProperty?.id}}
                        value={household}
                        onChange={field.onChange}
                      />
                    )}
                  </FormControl>
                  {form.formState.errors.household && (
                    <FormError error={form.formState.errors.household.message} />
                  )}
                </FormItem>
              );
            }}
          />
        )}
        {!safelisting &&
        isAmenityKeyRestricted(
          effectiveHousehold as AmenityRestrictionCheckSource | null,
          "safelisting",
        ) ? (
          <HouseholdAmenityRestrictionBanner />
        ) : null}
        {canShowPermitFlow ? (
          <>
            <div className={SHEET_PERMIT_FLOW_GAP}>
            <FormField
              control={form.control}
              name="dates_range"
              render={({field}) => (
                <FormItem className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                    <FieldHelpLabel helpKey={SAFELISTING_FORM_HELP_KEYS.datesRange} showIcon={false}>
                      <FormLabel required className="shrink-0">
                        {coverageFormCopy?.dateFieldLabel ?? "Dates (from – to)"}
                      </FormLabel>
                    </FieldHelpLabel>
                    {coverageFormCopy?.allowedHoursLine ? (
                      <>
                        <span className="text-muted-foreground text-sm" aria-hidden>
                          ·
                        </span>
                        <span className="text-muted-foreground text-sm font-normal leading-snug">
                          {coverageFormCopy.allowedHoursLine}
                        </span>
                      </>
                    ) : null}
                  </div>
                  <SafelistingCoverageRulesCallout
                    loading={propertyTypesPending}
                    todayWindowEnded={todayCoverageWindowEnded}
                  />
                  {quickPickAnchors && anchorQuickPickLabels ? (
                    <SafelistingCoverageAnchorQuickPicks
                      likelyAnchor={quickPickAnchors.primary}
                      nextAnchor={quickPickAnchors.secondary}
                      currentLabel={anchorQuickPickLabels.current}
                      nextLabel={anchorQuickPickLabels.next}
                      sectionLabel={
                        coverageFormCopy?.dateWord === "night" ? "Which night?" : "Which date?"
                      }
                      selectedFromAnchor={selectedFromAnchor}
                      onSelectAnchor={(anchor) => applyCoverageAnchor(anchor, field.onChange)}
                    />
                  ) : null}
                  <FormControl>
                    <DatePickerWithRange
                      showClear={!hasLockedPastCoverage}
                      completeRangeOnClose
                      date={field.value}
                      disabled={(date: Date) => {
                        if (!date) return true;
                        if (isLockedCoveredCalendarDate(date, editDateLocks)) return true;
                        return !isSafelistingCalendarDateSelectable(
                          date,
                          rangeHours,
                          propertyTimeZone,
                        );
                      }}
                      onChange={(range: {from?: Date; to?: Date}) => {
                        setCoverageAnchorSelectionSource("calendar");
                        applyDatesRangeChange(range, field.onChange);
                      }}
                      {...(!hasLockedPastCoverage
                        ? {
                            onClear: () => {
                              setCoverageAnchorSelectionSource("default");
                              applyDatesRangeChange(
                                {from: undefined, to: undefined},
                                field.onChange,
                              );
                            },
                          }
                        : {})}
                    />
                  </FormControl>
                  <SafelistingCoveragePreviewPanel
                    preview={coveragePreview}
                    rangeHours={rangeHours}
                    timeZone={propertyTimeZone}
                    mismatch={inlineCoverageAnchorMismatch}
                    onUseLikelyAnchor={
                      inlineCoverageAnchorMismatch
                        ? () =>
                            applyCoverageAnchor(
                              inlineCoverageAnchorMismatch.likelyAnchor,
                              field.onChange,
                              "quick_pick",
                            )
                        : undefined
                    }
                  />
                  <FormField
                    control={form.control}
                    name="excluded_dates"
                    render={({field: excludeField}) => (
                      <FormItem>
                        <FieldHelpLabel
                          helpKey={SAFELISTING_FORM_HELP_KEYS.excludedDates}
                          showIcon={false}
                        >
                          <FormLabel>Exclude {excludeDateWordPlural}</FormLabel>
                        </FieldHelpLabel>
                        <FormControl>
                          <Popover open={excludePopoverOpen} onOpenChange={setExcludePopoverOpen}>
                            <PopoverTrigger
                              render={
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={!rangeComplete || !canAddExclude}
                                  className="h-10 w-full justify-start font-normal"
                                />
                              }
                            >
                              Exclude {excludeDateWord}
                              <CalendarIcon className="ml-auto size-4" />
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={undefined}
                                onSelect={handleExcludeDateSelect}
                                disabled={(date) => {
                                  if (!from || !to) return true;
                                  if (isLockedCoveredCalendarDate(date, editDateLocks)) {
                                    return true;
                                  }
                                  if (
                                    !isSafelistingCalendarDateSelectable(
                                      date,
                                      rangeHours,
                                      propertyTimeZone,
                                    ) ||
                                    !isDateInRange(date, from, to)
                                  ) {
                                    return true;
                                  }
                                  const str = toYyyyMmDd(date);
                                  if (excludeField.value.includes(str)) return true;
                                  return false;
                                }}
                                defaultMonth={from}
                              />
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        {form.formState.errors.excluded_dates ? (
                          <FormError error={form.formState.errors.excluded_dates.message} />
                        ) : null}
                        {excludedDates.length > 0 ? (
                          <ul className="flex flex-wrap gap-2">
                            {excludedDates.map((dateStr) => (
                              <li key={dateStr}>
                                <span className="bg-muted inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm">
                                  {dateStr}
                                  <button
                                    type="button"
                                    disabled={isLockedExcludedDate(dateStr, editDateLocks)}
                                    onClick={() => removeExcludedDate(dateStr)}
                                    className="hover:bg-muted-foreground/20 rounded p-0.5 disabled:pointer-events-none disabled:opacity-50"
                                    aria-label={`Remove ${dateStr}`}
                                  >
                                    <XIcon className="size-3.5" />
                                  </button>
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </FormItem>
                    )}
                  />
                  {form.formState.errors.dates_range ? (
                    <FormError error={form.formState.errors.dates_range.message} />
                  ) : null}
                </FormItem>
              )}
            />
            <SafelistingLimitBanner
              accountability={accountability}
              totalDays={permitDays}
              dateWord={excludeDateWord}
              showStaffOverrideFields={showStaffOverrideFields}
            />

            <div className="border-border/60 flex flex-col gap-4 border-t pt-4">
            <div className={RECIPIENT_FIELDS_ROW}>
              <FormField
                control={form.control}
                name="recipient_name"
                render={({field}) => (
                  <FormItem className="min-w-0">
                    <FieldHelpLabel helpKey={SAFELISTING_FORM_HELP_KEYS.recipientName} showIcon={false}>
                      <FormLabel required>Recipient name</FormLabel>
                    </FieldHelpLabel>
                    <FormControl>
                      <Input type="text" autoComplete="name" {...field} />
                    </FormControl>
                    {form.formState.errors.recipient_name && (
                      <FormError error={form.formState.errors.recipient_name.message} />
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="recipient_phone"
                render={({field}) => (
                  <FormItem className="min-w-0">
                    <FieldHelpLabel helpKey={SAFELISTING_FORM_HELP_KEYS.recipientPhone} showIcon={false}>
                      <FormLabel>Recipient phone</FormLabel>
                    </FieldHelpLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder="(555) 123-4567"
                        value={formatUsPhone(field.value ?? "")}
                        onChange={(event) =>
                          field.onChange(normalizeUsPhoneDigits(event.target.value))
                        }
                      />
                    </FormControl>
                    {form.formState.errors.recipient_phone ? (
                      <FormError error={form.formState.errors.recipient_phone.message} />
                    ) : null}
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="recipient_email"
              render={({field}) => (
                <FormItem className="min-w-0">
                  <FieldHelpLabel helpKey={SAFELISTING_FORM_HELP_KEYS.recipientEmail} showIcon={false}>
                    <FormLabel required>Recipient email</FormLabel>
                  </FieldHelpLabel>
                  <FormControl>
                    <Input type="email" autoComplete="email" {...field} />
                  </FormControl>
                  {form.formState.errors.recipient_email && (
                    <FormError error={form.formState.errors.recipient_email.message} />
                  )}
                </FormItem>
              )}
            />
            {showStaffNoteFields ? (
            <>
            <FormField
              control={form.control}
              name="notes"
              render={({field}) => (
                <FormItem>
                  <FieldHelpLabel helpKey={SAFELISTING_FORM_HELP_KEYS.notes} showIcon={false}>
                    <FormLabel>Public notes</FormLabel>
                  </FieldHelpLabel>
                  <FormControl>
                    <Textarea className="resize-none" rows={2} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="private_notes"
              render={({field}) => (
                <FormItem>
                  <FieldHelpLabel helpKey={SAFELISTING_FORM_HELP_KEYS.privateNotes} showIcon={false}>
                    <FormLabel>Private notes</FormLabel>
                  </FieldHelpLabel>
                  <FormControl>
                    <Textarea className="resize-none" rows={2} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            </>
            ) : null}
            </div>
            </div>
            {showStaffOverrideFields ? (
            <HelpTarget target={SAFELISTING_FORM_HELP_KEYS.overrides} className="border-border/60 flex flex-col gap-3 border-t pt-6">
              <SafelistingOverrideHelpCard
                variant="create"
                autoExpand={expandOverrideRecipe}
                sectionTitle="Overrides"
              />
              <FormField
                control={form.control}
                name="override_count_toward_total"
                render={({field}) => (
                  <FormItem className="flex flex-row items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <FieldHelpLabel
                        helpKey={SAFELISTING_FORM_HELP_KEYS.overrideCountTowardTotal}
                        showIcon={false}
                      >
                        <FormLabel
                          htmlFor="override_count_toward_total"
                          className={SAFELISTING_OVERRIDE_SWITCH_LABEL_CLASS}
                        >
                          Override count toward total
                        </FormLabel>
                      </FieldHelpLabel>
                      <p className={SAFELISTING_OVERRIDE_FIELD_HINT_CLASS}>
                        {SAFELISTING_OVERRIDE_FIELD_HINTS.override_count_toward_total}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        id="override_count_toward_total"
                        name="override_count_toward_total"
                        disabled={Boolean(safelisting)}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="override_limit"
                render={({field}) => (
                  <FormItem className="flex flex-row items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <FieldHelpLabel
                        helpKey={SAFELISTING_FORM_HELP_KEYS.overrideLimit}
                        showIcon={false}
                      >
                        <FormLabel htmlFor="override_limit" className={SAFELISTING_OVERRIDE_SWITCH_LABEL_CLASS}>
                          Override limit
                        </FormLabel>
                      </FieldHelpLabel>
                      <p className={SAFELISTING_OVERRIDE_FIELD_HINT_CLASS}>
                        {SAFELISTING_OVERRIDE_FIELD_HINTS.override_limit}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        id="override_limit"
                        name="override_limit"
                        disabled={Boolean(safelisting)}
                        checked={field.value}
                        onCheckedChange={(v) => {
                          field.onChange(v);
                          if (!v) {
                            form.setValue("override_reason", "");
                            form.clearErrors("override_reason");
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              {overrideLimit ? (
                <FormField
                  control={form.control}
                  name="override_reason"
                  render={({field, formState: {errors}}) => (
                    <FormItem>
                      <FieldHelpLabel
                        helpKey={SAFELISTING_FORM_HELP_KEYS.overrideReason}
                        showIcon={false}
                      >
                        <FormLabel required className={SAFELISTING_OVERRIDE_SWITCH_LABEL_CLASS}>
                          Override reason
                        </FormLabel>
                      </FieldHelpLabel>
                      <p className={`${SAFELISTING_OVERRIDE_FIELD_HINT_CLASS} mb-1.5`}>
                        {SAFELISTING_OVERRIDE_FIELD_HINTS.override_reason}
                      </p>
                      <FormControl>
                        <Textarea
                          className="resize-none"
                          rows={2}
                          {...field}
                          disabled={Boolean(safelisting)}
                        />
                      </FormControl>
                      {errors.override_reason ? (
                        <FormError error={errors.override_reason.message} />
                      ) : null}
                    </FormItem>
                  )}
                />
              ) : null}
              <FormField
                control={form.control}
                name="override_days_in_a_row"
                render={({field}) => (
                  <FormItem className="flex flex-row items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <FieldHelpLabel
                        helpKey={SAFELISTING_FORM_HELP_KEYS.overrideDaysInARow}
                        showIcon={false}
                      >
                        <FormLabel
                          htmlFor="override_days_in_a_row"
                          className={SAFELISTING_OVERRIDE_SWITCH_LABEL_CLASS}
                        >
                          Override Property Manager
                        </FormLabel>
                      </FieldHelpLabel>
                      <p className={SAFELISTING_OVERRIDE_FIELD_HINT_CLASS}>
                        {SAFELISTING_OVERRIDE_FIELD_HINTS.override_days_in_a_row}
                      </p>
                    </div>
                    <FormControl>
                      <Switch
                        id="override_days_in_a_row"
                        name="override_days_in_a_row"
                        disabled={Boolean(safelisting)}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </HelpTarget>
            ) : null}

            <div
              className={`border-border/60 bg-background sticky bottom-0 z-10 border-t py-4 ${SHEET_STICKY_FOOTER_X}`}
            >
              <Button
                className="w-full"
                type="submit"
                disabled={isSubmitting || isSubmitDisabled}
                data-help-target={SAFELISTING_FORM_HELP_KEYS.submit}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">
            {hideHouseholdField ? "Select a vehicle to continue." : "Select a household to continue."}
          </p>
        )}
      </form>
    </Form>
  );
}
